import React, { useState, useEffect, useRef, useMemo } from 'react';
import TransformerFunctions from '../../store/transformer/functions';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import { IAuthState, IRootState } from '../../@types/redux';
import { connect } from 'react-redux';
import LinearProgress from '@material-ui/core/LinearProgress';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import TransformerInfo from './Info';
import Drawer from '@material-ui/core/Drawer';
import AppBar from '@material-ui/core/AppBar';
import { withRouter, RouteComponentProps } from 'react-router';
import { ITransformer } from '../../@types/model/transformer/transformer';
import CustomMapBox, { IMapMarker } from '../customComponents/map/CustomMapBox';
import { TRANSFORMER_MAP_IMAGES } from '../../appConstants';
import TransformerMapSideView from './MapSideView';

interface ITransformerMapProps extends RouteComponentProps<{
    Code : string;
}, Record<string, unknown>, Record<string, unknown>> {
    auth : IAuthState;
    transformers : Array<ITransformer>;
    isLoadingTransformers : boolean;
}

const TransformerMap = (props : ITransformerMapProps) : React.ReactElement => {
    const [selectedTransformer, setSelectedTransformer] = useState<ITransformer | null>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/
    
    useEffect(() => {
        loadData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const loadData = async () => {
        await TransformerFunctions.getList();

        const urlParams = new URLSearchParams(props.location.search);
        const epNumber = urlParams.get('Code') as string;
        onMarkerClick(epNumber);
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onMarkerClick = (id: string): void => {
        const transformer = props.transformers.find(n => n.EPNumber === id);

        if (transformer) {        
            setSelectedTransformer(transformer);
        }
    };

    const onMoreInfo = (): void => {
        if (selectedTransformer) {
            TransformerFunctions.setSelected(Object.assign({}, selectedTransformer));
        }
    };

    const onCloseDrawer = (): void => {
        setSelectedTransformer(null);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const filteredTransformers = useMemo<Array<ITransformer>>(() => {
        return props.transformers.slice().filter(n => n.Geom && (n.IsActive === undefined || n.IsActive));
    }, [props.transformers]);

    const markers = useMemo<Array<IMapMarker>>(() => {
        return filteredTransformers.map(n => ({
            coordinates: [n.Geom?.longitude ?? 0, n.Geom?.latitude ?? 0],
            iconSize: 0.35,
            icon: '1',
            id: n.EPNumber,
        }));
    }, [filteredTransformers]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div className={'fdc flx1 p10 mh0 mw0'}>
            <div className={'flx1 fdc mh0 mw0'}>
                <Toolbar>
                    <Typography variant='h5' color='inherit' className={'fw500'}>
                        TRANSFORMERS
                    </Typography>
                    <span className={'flx1'} />
                    <div className={'fdr pt5 jcfe aic'}>
                        <div className={'fdr pt5'}>
                            <span style={{ paddingRight: '15px' }}><b>Total: </b>{filteredTransformers.length}</span>
                        </div>
                    </div>
                </Toolbar>
                <div className='mnh4'>
                    { props.isLoadingTransformers && <LinearProgress />}
                </div>
                <Paper className={'flx1 fdr oxh'}>
                    {      
                        filteredTransformers.length > 0 &&
                        <CustomMapBox
                            id='transformer_map'
                            enableAreas
                            geoLocationControl
                            navigationControl
                            showScale
                            layerControls
                            images={TRANSFORMER_MAP_IMAGES}
                            onMarkerClick={onMarkerClick}
                            markers={markers}
                            selectedMarker={selectedTransformer?.EPNumber}
                        />
                    }
                </Paper>
            </div>
            <Drawer open={!!selectedTransformer} anchor='right' variant='persistent'>
                <div className={'w500 flx1 fdc'}>
                    <AppBar className='posr bcpd' position='static'>
                        <Toolbar variant='dense' className={'pl15 pr15'}>
                            <div className='h60' />
                            <IconButton color={'inherit'} size={'small'} onClick={onMoreInfo}><Icon fontSize={'large'} color={'inherit'}>info_outline</Icon></IconButton>
                            <Typography color='inherit' variant='h5' className={'flx1 ml10'} noWrap={true}>
                                {!!selectedTransformer && selectedTransformer.EPNumber}
                            </Typography>
                            <IconButton color={'inherit'} size={'small'} onClick={onCloseDrawer}><Icon fontSize={'large'} color={'inherit'}>close</Icon></IconButton>
                        </Toolbar>
                    </AppBar>
                    {
                        selectedTransformer && 
                        <TransformerMapSideView transformer={selectedTransformer} />
                    }
                </div>
            </Drawer>
            <TransformerInfo  />
        </div>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        transformers: state.transformer.transformers,
        isLoadingTransformers: state.transformer.loading,
        auth: state.auth,
    };
};

export default connect(
    mapStateToProps,
)(withRouter(TransformerMap));
